import { useMemo } from 'react';
import { Box, GradientButton, Stack } from '@lingoda/ui';
import { trans } from '@lingoda/i18n';
import type { UserCreditsQuery } from '@lingoda/graphql';
import { Location } from '@lingoda/graphql';
import { ContentBlockTypography, findContentBlock } from '@lingoda/content-blocks';
import {
    SettingsPages,
    creditsConversionPath,
    studentSettingsPath,
    studentStorePath,
    subscriptionsStorePath,
} from '@lingoda/router';
import { colorsPalette } from '@lingoda/ui/theme';
import { CreditAmounts } from '@lingoda/credits';
import { ConvertIcon, Membership, ShoppingCartIcon24 } from '@lingoda/ui/icons/lingodaIcons';
import { currentSubscriptionSelector } from '@lingoda/subscriptions';
import { useSelector } from '@lingoda/hooks';
import { useIsFFreeTrial, useIsFFreeTrialOrWasInTrial } from '@lingoda/f-free-trial';
import { MenuItemLink } from '../../MenuComponents';

type UserCreditsData = Partial<
    Pick<
        UserCreditsQuery['studentCreditsInfo'],
        'groupCredits' | 'privateCredits' | 'contentBlocks' | 'canBuyExtraCredits'
    >
>;

interface StudentCreditsInfoProps extends UserCreditsData {
    isLoading?: boolean;
    onCTAClick?: () => void;
    hasSubscription?: boolean;
    creditsOrientation?: 'row' | 'column';
}

export const StudentCreditsInfo = ({
    isLoading,
    onCTAClick,
    groupCredits,
    contentBlocks,
    privateCredits,
    hasSubscription,
    canBuyExtraCredits,
    creditsOrientation = 'row',
}: StudentCreditsInfoProps) => {
    const renew = useMemo(
        () => contentBlocks && findContentBlock(contentBlocks, Location.CreditsRenew),
        [contentBlocks],
    );
    const currentSubscription = useSelector(currentSubscriptionSelector);
    const isFFreeTrialOrWasInTrial = useIsFFreeTrialOrWasInTrial();
    const isFFreeTrial = useIsFFreeTrial();

    return (
        <Stack spacing={2} mt={2} pb={1}>
            <Stack spacing={2} px={2}>
                <CreditAmounts
                    isLoading={isLoading}
                    groupCredits={groupCredits}
                    privateCredits={privateCredits}
                    orientation={creditsOrientation}
                />
                {(!hasSubscription || isFFreeTrialOrWasInTrial) && !isLoading ? (
                    <GradientButton fullWidth onClick={onCTAClick} href={subscriptionsStorePath()}>
                        {trans('navigation.prices', {}, 'public-common')}
                    </GradientButton>
                ) : (
                    <Box>
                        {renew && (
                            <ContentBlockTypography
                                textColor={colorsPalette.blackAlpha[70]}
                                {...renew}
                            />
                        )}
                    </Box>
                )}
            </Stack>
            {hasSubscription && (
                <Box>
                    <MenuItemLink
                        to={studentSettingsPath({ page: SettingsPages.Membership })}
                        onClick={onCTAClick}
                        sx={{ color: colorsPalette.blackAlpha[70], width: '100%' }}
                    >
                        <Membership />
                        {trans('btn-manage-subscription', {}, 'student-common')}
                    </MenuItemLink>

                    {canBuyExtraCredits && (
                        <MenuItemLink
                            to={studentStorePath()}
                            onClick={onCTAClick}
                            sx={{ color: colorsPalette.blackAlpha[70], width: '100%' }}
                        >
                            <ShoppingCartIcon24 />
                            {trans('buy-extra-class-credits', {}, 'student-common')}
                        </MenuItemLink>
                    )}
                    {(currentSubscription?.isRecurring || isFFreeTrial) && (
                        <MenuItemLink
                            to={creditsConversionPath()}
                            onClick={onCTAClick}
                            sx={{ color: colorsPalette.blackAlpha[70], width: '100%' }}
                        >
                            <ConvertIcon />
                            {trans('convert-class-credits-title', {}, 'student-store')}
                        </MenuItemLink>
                    )}
                </Box>
            )}
        </Stack>
    );
};
